import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

function need(name,method){
  const x=g[name];
  if(!x||typeof x[method]!=='function') throw Error(name+'_'+method+'_REQUIRED');
  return x;
}
function stepOutput(ctx,name){
  const row=(ctx?.steps||[]).find(x=>x.name===name&&x.status==='OK');
  return row?row.output:null;
}
function arr(x){return Array.isArray(x)?x:[];}
function countCriticalGaps(xs){return arr(xs).filter(x=>x?.severity==='CRITICAL'||x?.critical===true).length;}
function countConflicts(xs){return arr(xs).filter(x=>x?.state==='CONFLICTED'||x?.conflict===true).length;}

function create(opts={}){
  const pipeline=need('ZooInvestResearchRuntimePipelineOrchestrator','create');
  const snapshot=need('ZooInvestResearchStateSnapshotEngine','snapshot');
  const meaningful=need('ZooInvestMeaningfulChangeDetector','scan');
  const thesis=need('ZooInvestThesisDriftEngine','challenge');
  const invalidation=need('ZooInvestConclusionInvalidationEngine','plan');
  const reopen=need('ZooInvestDependencyAwareResearchReopenEngine','reopenTasks');
  const integrity=need('ZooInvestResearchIntegrityHealthEngine','assess');
  const gate=need('ZooInvestResearchIntegrityDecisionGate','gate');
  const priority=need('ZooInvestDecisionChangePriorityEngine','prioritize');
  const brief=need('ZooInvestDailyDecisionBriefV3IntegrationEngine','build');

  function snapshotAdapter(ctx){
    return snapshot.snapshot(ctx.input||{});
  }

  function meaningfulAdapter(ctx){
    return meaningful.scan(arr(ctx?.input?.changeCandidates));
  }

  function thesisAdapter(ctx){
    const x=ctx?.input||{};
    const original=arr(x.originalThesisPillars);
    const current=arr(x.currentThesisPillars);
    if(!original.length||!current.length){
      return Object.freeze({
        symbol:x.symbol||null,
        state:'INSUFFICIENT_DATA',
        removed:Object.freeze([]),
        added:Object.freeze([]),
        purchasePriceRelevant:false,
        tradeSignal:false,
        prompt:'Thesis drift not assessed because evidence-backed thesis pillars are missing.'
      });
    }
    return thesis.challenge({
      symbol:x.symbol,
      original,
      current,
      verification:x.thesisVerification||'UNVERIFIED'
    });
  }

  function invalidationAdapter(ctx){
    const x=ctx?.input||{};
    if(!x.evidenceGraph||!arr(x.evidenceChanges).length){
      return Object.freeze({
        conclusions:Object.freeze([]),
        affectedCount:0,
        unaffectedCount:null,
        wholeCompanyInvalidated:false,
        companyQualityOverride:false,
        entryGateBypass:false,
        tradeSignal:false,
        capitalPermission:false,
        state:'NO_DEPENDENCY_CHANGE'
      });
    }
    return invalidation.plan(x.evidenceGraph,x.evidenceChanges);
  }

  function reopenAdapter(ctx){
    const inv=stepOutput(ctx,'invalidation');
    return reopen.reopenTasks(
      arr(ctx?.input?.researchTasks),
      arr(inv?.conclusions)
    );
  }

  function integrityAdapter(ctx){
    const x=ctx?.input||{};
    const m=x.integrityMetrics||{};
    return integrity.assess({
      symbol:x.symbol,
      evidenceCoverage:m.evidenceCoverage??null,
      verifiedCoverage:m.verifiedCoverage??null,
      freshnessCoverage:m.freshnessCoverage??null,
      lineageCoverage:m.lineageCoverage??null,
      contractErrors:Number(m.contractErrors||0),
      consistencyErrors:Number(m.consistencyErrors||0),
      orphanClaims:Number(m.orphanClaims||0),
      orphanConclusions:Number(m.orphanConclusions||0),
      unresolvedConflicts:Number(m.unresolvedConflicts||countConflicts(x.evidenceConflicts)),
      consistencyReviews:Number(m.consistencyReviews||0)
    });
  }

  function gateAdapter(ctx){
    const x=ctx?.input||{};
    const integ=stepOutput(ctx,'integrity')||{};
    const m=x.integrityMetrics||{};
    return gate.gate({
      integrityState:integ.state||'INSUFFICIENT_DATA',
      readiness:x.readiness||'REVIEW',
      unresolvedCriticalGaps:Number(m.unresolvedCriticalGaps||countCriticalGaps(x.openGaps)),
      unresolvedConflicts:Number(m.unresolvedConflicts||countConflicts(x.evidenceConflicts)),
      staleCriticalEvidence:m.staleCriticalEvidence===true,
      contractErrors:Number(m.contractErrors||0),
      consistencyErrors:Number(m.consistencyErrors||0)
    });
  }

  function priorityAdapter(ctx){
    const x=ctx?.input||{};
    const drift=stepOutput(ctx,'thesisDrift')||{};
    const inv=stepOutput(ctx,'invalidation')||{};
    const integGate=stepOutput(ctx,'integrityGate')||{};
    const meaningfulOut=stepOutput(ctx,'meaningfulChange')||{};
    const row={
      symbol:x.symbol,
      scope:x.scope||'RESEARCH',
      deltaState:arr(meaningfulOut.meaningful).length?'CHANGED':'UNCHANGED',
      readinessBefore:x.previousReadiness||'REVIEW',
      readinessAfter:integGate.readiness||'REVIEW',
      thesisDrift:drift.state||'INSUFFICIENT_DATA',
      compoundState:x.compoundSignal||'NO_COMPOUND_SIGNAL',
      invalidatedCount:Number(inv.affectedCount||0),
      newGapCount:arr(x.openGaps).length,
      eventWithin24h:x.eventWithin24h===true,
      evidenceChanged:arr(meaningfulOut.meaningful).length>0
    };
    const ranked=priority.prioritize([row],1);
    return ranked[0]||null;
  }

  function briefAdapter(ctx){
    const x=ctx?.input||{};
    const meaningfulOut=stepOutput(ctx,'meaningfulChange')||{};
    const drift=stepOutput(ctx,'thesisDrift');
    const inv=stepOutput(ctx,'invalidation')||{};
    const reopened=stepOutput(ctx,'reopen')||[];
    const integrityGate=stepOutput(ctx,'integrityGate')||{};
    return brief.build({
      symbol:x.symbol,
      scope:x.scope||'RESEARCH',
      integrityGate,
      meaningfulChanges:arr(meaningfulOut.meaningful).map(v=>({
        text:v.summary||v.label||v.key||'Verified material evidence changed.',
        priority:50
      })),
      thesisDrift:drift&&drift.state&&!['STABLE','INSUFFICIENT_DATA'].includes(drift.state)?[{
        text:drift.prompt||drift.state,
        priority:drift.state==='MATERIAL_DRIFT'?90:60
      }]:[],
      invalidations:arr(inv.conclusions).map(v=>({
        summary:`${v.label||v.conclusionId||'Conclusion'}: ${v.action}`,
        action:v.action,
        severity:v.action==='INVALIDATE'?90:60
      })),
      reopenTasks:arr(reopened),
      openGaps:arr(x.openGaps),
      researchQueue:x.researchQueue||null,
      causeTransmission:arr(x.causeTransmission),
      companyTransmission:arr(x.companyTransmission),
      compoundDelta:arr(x.compoundDelta),
      evidenceFingerprint:x.evidenceFingerprint||null,
      thesisFingerprint:x.thesisFingerprint||null
    });
  }

  const engines=Object.freeze({
    snapshot:snapshotAdapter,
    meaningfulChange:meaningfulAdapter,
    thesisDrift:thesisAdapter,
    invalidation:invalidationAdapter,
    reopen:reopenAdapter,
    integrity:integrityAdapter,
    integrityGate:gateAdapter,
    priority:priorityAdapter,
    brief:briefAdapter
  });

  const runtime=pipeline.create({
    engines,
    journal:opts.journal||null,
    persist:opts.persist||null
  });

  return Object.freeze({
    VERSION,
    run:runtime.run,
    engines,
    capabilities:Object.freeze({
      snapshot:true,
      meaningfulChange:true,
      thesisDrift:true,
      invalidation:true,
      reopen:true,
      integrity:true,
      integrityGate:true,
      priority:true,
      brief:true
    })
  });
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const c=create();
  ok('all nine pipeline engines configured',Object.values(c.capabilities).filter(Boolean).length===9);

  const base={
    symbol:'AAA',scope:'OWNED',
    readiness:'REVIEW',
    changeCandidates:[],
    openGaps:[],
    evidenceConflicts:[],
    integrityMetrics:{
      evidenceCoverage:.9,verifiedCoverage:.9,freshnessCoverage:.9,lineageCoverage:.9
    },
    originalThesisPillars:['MOAT','GROWTH'],
    currentThesisPillars:['MOAT','GROWTH'],
    thesisVerification:'VERIFIED',
    researchTasks:[]
  };
  const r=await c.run(base);
  ok('configured pipeline has no missing-engine errors',!r.errors.some(x=>x.reason==='MISSING_ENGINE'));
  ok('snapshot produced',!!r.snapshot?.fingerprint);
  ok('meaningful change engine ran',!!r.meaningfulChange);
  ok('thesis drift engine ran',r.thesisDrift?.state==='STABLE');
  ok('invalidation engine represented no change',r.invalidation?.state==='NO_DEPENDENCY_CHANGE');
  ok('reopen engine ran',Array.isArray(r.reopen));
  ok('integrity engine ran',r.integrity?.state==='HEALTHY');
  ok('integrity gate ran',r.integrityGate?.gateState==='PASS');
  ok('priority engine ran',r.priority?.symbol==='AAA');
  ok('brief engine ran',r.brief?.symbol==='AAA');
  ok('healthy configured pipeline ready',r.status==='READY');

  const sparse=await c.run({symbol:'BBB',scope:'RESEARCH'});
  ok('sparse integrity is insufficient',sparse.integrity?.state==='INSUFFICIENT_DATA');
  ok('sparse integrity requires review',sparse.integrityGate?.gateState==='REVIEW');
  ok('missing thesis pillars are not falsely stable',sparse.thesisDrift?.state==='INSUFFICIENT_DATA');

  const changed=await c.run({
    ...base,symbol:'CCC',
    changeCandidates:[{previous:1,current:2,verification:'VERIFIED',materiality:.9,key:'Revenue'}]
  });
  ok('verified material change detected',changed.meaningfulChange.meaningful.length===1);
  ok('priority sees evidence change',changed.priority.evidenceChanged===true);

  const drifted=await c.run({...base,symbol:'DDD',currentThesisPillars:['MOAT']});
  ok('thesis drift reaches real engine',drifted.thesisDrift.state==='REVIEW_DRIFT');

  ok('no universal score on composition root',!('score' in c));
  ok('no trade signal on composition root',!('tradeSignal' in c));
  ok('no capital permission on composition root',!('capitalPermission' in c));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestResearchPipelineCompositionRoot=Object.freeze({VERSION,create,selfTest});
})(engineContext);